import React, { useState, useEffect, useMemo } from 'react';
import './Admin.css';

/* ──────────────────────────────────────────────
   Helpers
────────────────────────────────────────────── */
function getToken() {
  try {
    const s = sessionStorage.getItem('supabase-auth-token');
    return s ? (JSON.parse(s).access_token ?? process.env.REACT_APP_SUPABASE_ANON_KEY) : process.env.REACT_APP_SUPABASE_ANON_KEY;
  } catch { return process.env.REACT_APP_SUPABASE_ANON_KEY; }
}

function apiFetch(path) {
  const url  = process.env.REACT_APP_SUPABASE_URL;
  const key  = process.env.REACT_APP_SUPABASE_ANON_KEY;
  return fetch(`${url}/rest/v1/${path}`, {
    headers: { apikey: key, Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
  });
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function initials(name) {
  if (!name) return '?';
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

const PLAN_COLORS = {
  starter:      { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0', label: 'Starter' },
  professional: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', label: 'Professional' },
  enterprise:   { bg: '#f3e8ff', color: '#7c3aed', border: '#e9d5ff', label: 'Enterprise' },
};

function PlanBadge({ plan }) {
  const p = PLAN_COLORS[plan?.toLowerCase()] ?? { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0', label: plan ?? 'Unknown' };
  return (
    <span className="adm-badge" style={{ background: p.bg, color: p.color, borderColor: p.border }}>
      {p.label}
    </span>
  );
}

/* ──────────────────────────────────────────────
   New Tenant Form
────────────────────────────────────────────── */
function NewTenantForm({ onCreated }) {
  const [form, setForm] = useState({ name: '', admin_email: '', plan: 'professional', industry: '' });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.admin_email.trim()) {
      setError('Organization name and admin email are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const url = process.env.REACT_APP_SUPABASE_URL;
      const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
      const res = await fetch(`${url}/rest/v1/organizations`, {
        method: 'POST',
        headers: {
          apikey: key,
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
          Prefer: 'return=representation',
        },
        body: JSON.stringify({
          name:        form.name.trim(),
          admin_email: form.admin_email.trim(),
          plan:        form.plan,
          industry:    form.industry.trim() || null,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message ?? `HTTP ${res.status}`);
      }

      const [org] = await res.json();
      setSuccess(`Organization "${org?.name ?? form.name}" created successfully.`);
      setForm({ name: '', admin_email: '', plan: 'professional', industry: '' });
      onCreated && onCreated();
    } catch (err) {
      setError('Failed to create organization: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error   && <div className="adm-alert adm-alert--danger"><i className="bi bi-exclamation-circle" />{error}</div>}
      {success && <div className="adm-alert adm-alert--success"><i className="bi bi-check-circle" />{success}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div className="adm-form-group">
          <label className="adm-label">Organization Name *</label>
          <input className="adm-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Acme Corp" required />
        </div>
        <div className="adm-form-group">
          <label className="adm-label">Admin Email *</label>
          <input className="adm-input" type="email" value={form.admin_email} onChange={e => set('admin_email', e.target.value)} placeholder="admin@acme.com" required />
        </div>
        <div className="adm-form-group">
          <label className="adm-label">Subscription Plan</label>
          <select className="adm-select" value={form.plan} onChange={e => set('plan', e.target.value)}>
            <option value="starter">Starter — Free · 50 credits/mo</option>
            <option value="professional">Professional — $99/mo · 2,000 credits</option>
            <option value="enterprise">Enterprise — Custom · Unlimited</option>
          </select>
        </div>
        <div className="adm-form-group">
          <label className="adm-label">Industry</label>
          <input className="adm-input" value={form.industry} onChange={e => set('industry', e.target.value)} placeholder="e.g. Financial Services" />
        </div>
      </div>

      <button className="adm-btn adm-btn--primary" type="submit" disabled={saving}>
        {saving ? <><span className="adm-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Creating…</> : <><i className="bi bi-plus-circle" /> Create Organization</>}
      </button>
    </form>
  );
}

/* ──────────────────────────────────────────────
   Main Component
────────────────────────────────────────────── */
const TenantManagement = () => {
  const [tab, setTab]           = useState('list');
  const [profiles, setProfiles] = useState([]);
  const [orgs, setOrgs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const [refresh, setRefresh]   = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [pRes, oRes] = await Promise.all([
          apiFetch('profiles?select=id,organization_id,role,created_at&order=created_at.asc'),
          apiFetch('organizations?select=*&order=created_at.desc'),
        ]);

        const profileData = pRes.ok  ? await pRes.json()  : [];
        const orgData     = oRes.ok  ? await oRes.json()  : [];

        setProfiles(profileData);
        setOrgs(orgData);
      } catch (err) {
        setError('Failed to load tenant data. ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [refresh]);

  /* Derive tenant list from orgs + profile counts */
  const tenants = useMemo(() => {
    if (orgs.length > 0) {
      return orgs.map(org => ({
        ...org,
        userCount: profiles.filter(p => p.organization_id === org.id).length,
      }));
    }
    /* Fallback: derive from profiles when no organizations table */
    const byOrg = {};
    profiles.forEach(p => {
      if (!p.organization_id) return;
      if (!byOrg[p.organization_id]) {
        byOrg[p.organization_id] = { id: p.organization_id, name: p.organization_id, created_at: p.created_at, plan: null, userCount: 0 };
      }
      byOrg[p.organization_id].userCount++;
      if (p.created_at < byOrg[p.organization_id].created_at) {
        byOrg[p.organization_id].created_at = p.created_at;
      }
    });
    return Object.values(byOrg);
  }, [orgs, profiles]);

  const filtered = useMemo(() => {
    if (!search.trim()) return tenants;
    const q = search.toLowerCase();
    return tenants.filter(t =>
      (t.name ?? '').toLowerCase().includes(q) ||
      (t.admin_email ?? '').toLowerCase().includes(q) ||
      (t.industry ?? '').toLowerCase().includes(q)
    );
  }, [tenants, search]);

  const stats = useMemo(() => ({
    total:        tenants.length,
    totalUsers:   profiles.length,
    enterprise:   tenants.filter(t => t.plan === 'enterprise').length,
    professional: tenants.filter(t => t.plan === 'professional').length,
  }), [tenants, profiles]);

  return (
    <div className="adm-container">

      <div className="adm-page-header">
        <h1 className="adm-page-title"><i className="bi bi-buildings" />Tenant Management</h1>
        <p className="adm-page-subtitle">Create, view, and manage all tenant organizations on the platform.</p>
      </div>
      <hr className="adm-divider" />

      <div className="adm-stats-row">
        {[
          { label: 'Total Tenants',   value: stats.total,        icon: 'bi-buildings',     color: '#006B54' },
          { label: 'Total Users',     value: stats.totalUsers,   icon: 'bi-people',         color: '#1d4ed8' },
          { label: 'Enterprise',      value: stats.enterprise,   icon: 'bi-building-check', color: '#7c3aed' },
          { label: 'Professional',    value: stats.professional, icon: 'bi-briefcase',      color: '#0891b2' },
        ].map(s => (
          <div key={s.label} className="adm-stat-card" style={{ borderTopColor: s.color }}>
            <div className="adm-stat-icon" style={{ background: s.color + '18', color: s.color }}>
              <i className={`bi ${s.icon}`} />
            </div>
            <div className="adm-stat-body">
              <div className="adm-stat-value">{loading ? '—' : s.value}</div>
              <div className="adm-stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="adm-tabs">
        <button className={`adm-tab ${tab === 'list' ? 'active' : ''}`} onClick={() => setTab('list')}>
          <i className="bi bi-list-ul" style={{ marginRight: 6 }} />View Tenants
        </button>
        <button className={`adm-tab ${tab === 'new' ? 'active' : ''}`} onClick={() => setTab('new')}>
          <i className="bi bi-plus-circle" style={{ marginRight: 6 }} />New Tenant
        </button>
      </div>

      {tab === 'list' && (
        <div className="adm-card">
          <div className="adm-card-header">
            <span className="adm-card-title">All Organizations</span>
            <div className="adm-search-wrap" style={{ width: 240 }}>
              <i className="bi bi-search adm-search-icon" />
              <input
                className="adm-input"
                style={{ padding: '7px 10px 7px 30px', fontSize: '0.82rem' }}
                placeholder="Search tenants…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="adm-card-body--flush">
            {error && <div className="adm-alert adm-alert--danger" style={{ margin: 16 }}><i className="bi bi-exclamation-circle" />{error}</div>}
            {loading ? (
              <div className="adm-empty"><div className="adm-spinner" /><span className="adm-empty-text">Loading tenants…</span></div>
            ) : filtered.length === 0 ? (
              <div className="adm-empty">
                <i className="bi bi-buildings" />
                <span className="adm-empty-text">{search ? 'No tenants match your search.' : 'No tenant organizations yet. Create one to get started.'}</span>
              </div>
            ) : (
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>Organization</th>
                      <th>Industry</th>
                      <th>Plan</th>
                      <th>Users</th>
                      <th>Created</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(t => (
                      <tr key={t.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div className="adm-avatar" style={{ background: '#006B54' }}>
                              {initials(t.name)}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{t.name ?? t.id}</div>
                              {t.admin_email && <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{t.admin_email}</div>}
                            </div>
                          </div>
                        </td>
                        <td style={{ color: '#64748b', fontSize: '0.82rem' }}>{t.industry ?? '—'}</td>
                        <td><PlanBadge plan={t.plan} /></td>
                        <td style={{ fontWeight: 600 }}>{t.userCount}</td>
                        <td style={{ color: '#64748b', fontSize: '0.82rem' }}>{formatDate(t.created_at)}</td>
                        <td>
                          <span className={`adm-badge adm-badge--${t.status === 'inactive' ? 'red' : 'green'}`}>
                            <i className={`bi bi-${t.status === 'inactive' ? 'x-circle' : 'check-circle'}`} />
                            {t.status === 'inactive' ? 'Inactive' : 'Active'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {!loading && (
            <div className="adm-footer-note" style={{ padding: '12px 20px' }}>
              Showing {filtered.length} of {tenants.length} organization{tenants.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}

      {tab === 'new' && (
        <div className="adm-card" style={{ maxWidth: 740 }}>
          <div className="adm-card-header">
            <span className="adm-card-title">Create New Tenant Organization</span>
          </div>
          <div className="adm-card-body">
            <NewTenantForm onCreated={() => { setRefresh(r => r + 1); setTab('list'); }} />
          </div>
        </div>
      )}

    </div>
  );
};

export default TenantManagement;
